import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  CardActions,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Divider,
  Tooltip,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
  Snackbar,
  Breadcrumbs,
  Link,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Badge,
  Avatar,
  Rating,
  AlertTitle,
  Drawer,
  AppBar,
  Toolbar,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Save,
  Cancel,
  Visibility,
  Search,
  FilterList,
  Refresh,
  ExpandMore,
  Download,
  LocalShipping,
  Inventory,
  Business,
  Person,
  CheckCircle,
  Error,
  Warning,
  Info,
  Timeline,
  Assessment,
  Analytics,
  Schedule,
  AttachMoney,
  Verified,
  Lock,
  Print,
  Share,
} from '@mui/icons-material';
import { useRbac } from '../../context/RbacContext';

interface Supplier {
  id: string;
  name: string;
  contact: string;
  email: string;
  address: string;
  status: 'active' | 'inactive';
  rating: number;
  createdAt: string;
}

interface PurchaseOrder {
  id: string;
  supplier: Supplier;
  items: Array<{
    id: string;
    name: string;
    quantity: number;
    unit: string;
    price: number;
  }>;
  status: 'pending' | 'approved' | 'received' | 'cancelled';
  total: number;
  createdAt: string;
  expectedDelivery: string;
}

interface SupplyChainAnalytics {
  totalSuppliers: number;
  activeSuppliers: number;
  totalOrders: number;
  pendingOrders: number;
  receivedOrders: number;
  totalSpend: number;
  topSuppliers: Array<{
    name: string;
    totalOrders: number;
    totalSpend: number;
  }>;
  orderTrends: Array<{
    date: string;
    orders: number;
    spend: number;
  }>;
}

export const SupplyChainManagement: React.FC = () => {
  const { hasPermission } = useRbac();
  const [tabIndex, setTabIndex] = useState(0);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [orders, setOrders] = useState<PurchaseOrder[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState<'supplier' | 'order' | null>(null);
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<PurchaseOrder | null>(null);
  const [formSupplier, setFormSupplier] = useState<Partial<Supplier>>({});
  const [formOrder, setFormOrder] = useState<Partial<PurchaseOrder>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState({
    supplierStatus: 'all',
    orderStatus: 'all',
  });

  // Mock analytics
  const [analytics, setAnalytics] = useState<SupplyChainAnalytics>({
    totalSuppliers: 0,
    activeSuppliers: 0,
    totalOrders: 0,
    pendingOrders: 0,
    receivedOrders: 0,
    totalSpend: 0,
    topSuppliers: [],
    orderTrends: [],
  });

  // Mock data
  useEffect(() => {
    const mockSuppliers: Supplier[] = [
      {
        id: '1',
        name: 'Alpha Materials',
        contact: 'John Doe',
        email: 'alpha@materials.com',
        address: '123 Main St, City',
        status: 'active',
        rating: 4.7,
        createdAt: '2024-01-01T00:00:00Z',
      },
      {
        id: '2',
        name: 'Beta Supplies',
        contact: 'Jane Smith',
        email: 'beta@supplies.com',
        address: '456 Market Ave, City',
        status: 'inactive',
        rating: 4.2,
        createdAt: '2024-01-10T00:00:00Z',
      },
    ];
    setSuppliers(mockSuppliers);

    const mockOrders: PurchaseOrder[] = [
      {
        id: '1',
        supplier: mockSuppliers[0],
        items: [
          { id: '1', name: 'Steel Rods', quantity: 100, unit: 'pcs', price: 5 },
          { id: '2', name: 'Plastic Sheets', quantity: 200, unit: 'sqm', price: 2 },
        ],
        status: 'pending',
        total: 900,
        createdAt: '2024-01-15T00:00:00Z',
        expectedDelivery: '2024-01-25T00:00:00Z',
      },
      {
        id: '2',
        supplier: mockSuppliers[1],
        items: [
          { id: '3', name: 'Copper Wire', quantity: 50, unit: 'kg', price: 10 },
        ],
        status: 'received',
        total: 500,
        createdAt: '2024-01-12T00:00:00Z',
        expectedDelivery: '2024-01-20T00:00:00Z',
      },
    ];
    setOrders(mockOrders);

    setAnalytics({
      totalSuppliers: mockSuppliers.length,
      activeSuppliers: mockSuppliers.filter(s => s.status === 'active').length,
      totalOrders: mockOrders.length,
      pendingOrders: mockOrders.filter(o => o.status === 'pending').length,
      receivedOrders: mockOrders.filter(o => o.status === 'received').length,
      totalSpend: mockOrders.reduce((sum, o) => sum + o.total, 0),
      topSuppliers: [
        { name: 'Alpha Materials', totalOrders: 5, totalSpend: 4500 },
        { name: 'Beta Supplies', totalOrders: 3, totalSpend: 2100 },
      ],
      orderTrends: [
        { date: '2024-01-10', orders: 2, spend: 1200 },
        { date: '2024-01-15', orders: 1, spend: 900 },
        { date: '2024-01-20', orders: 3, spend: 1800 },
      ],
    });
  }, []);

  const handleCreateSupplier = () => {
    setDialogType('supplier');
    setFormSupplier({});
    setOpenDialog(true);
  };

  const handleEditSupplier = (supplier: Supplier) => {
    setDialogType('supplier');
    setFormSupplier(supplier);
    setSelectedSupplier(supplier);
    setOpenDialog(true);
  };

  const handleSaveSupplier = () => {
    setLoading(true);
    setTimeout(() => {
      if (selectedSupplier) {
        setSuppliers(prev => prev.map(s => s.id === selectedSupplier.id ? { ...selectedSupplier, ...formSupplier } as Supplier : s));
        setSuccess('Supplier updated successfully');
      } else {
        setSuppliers(prev => [...prev, { ...formSupplier, id: Date.now().toString(), createdAt: new Date().toISOString(), rating: 0, status: 'active' } as Supplier]);
        setSuccess('Supplier created successfully');
      }
      setOpenDialog(false);
      setLoading(false);
      setSelectedSupplier(null);
      setFormSupplier({});
    }, 1000);
  };

  const handleDeleteSupplier = (supplierId: string) => {
    setSuppliers(prev => prev.filter(s => s.id !== supplierId));
    setSuccess('Supplier deleted successfully');
  };

  const handleCreateOrder = () => {
    setDialogType('order');
    setFormOrder({});
    setOpenDialog(true);
  };

  const handleEditOrder = (order: PurchaseOrder) => {
    setDialogType('order');
    setFormOrder(order);
    setSelectedOrder(order);
    setOpenDialog(true);
  };

  const handleSaveOrder = () => {
    setLoading(true);
    setTimeout(() => {
      if (selectedOrder) {
        setOrders(prev => prev.map(o => o.id === selectedOrder.id ? { ...selectedOrder, ...formOrder } as PurchaseOrder : o));
        setSuccess('Order updated successfully');
      } else {
        setOrders(prev => [...prev, { ...formOrder, id: Date.now().toString(), createdAt: new Date().toISOString(), status: 'pending', total: 0, expectedDelivery: new Date().toISOString() } as PurchaseOrder]);
        setSuccess('Order created successfully');
      }
      setOpenDialog(false);
      setLoading(false);
      setSelectedOrder(null);
      setFormOrder({});
    }, 1000);
  };

  const handleDeleteOrder = (orderId: string) => {
    setOrders(prev => prev.filter(o => o.id !== orderId));
    setSuccess('Order deleted successfully');
  };

  const filteredSuppliers = suppliers.filter(supplier => {
    const matchesSearch = supplier.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      supplier.contact.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filter.supplierStatus === 'all' || supplier.status === filter.supplierStatus;
    return matchesSearch && matchesStatus;
  });

  const filteredOrders = orders.filter(order => {
    const matchesSearch = order.supplier.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filter.orderStatus === 'all' || order.status === filter.orderStatus;
    return matchesSearch && matchesStatus;
  });

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <LocalShipping color="primary" />
        Supply Chain Management
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}
      {success && (
        <Snackbar open={!!success} autoHideDuration={6000} onClose={() => setSuccess(null)}>
          <Alert severity="success" onClose={() => setSuccess(null)}>{success}</Alert>
        </Snackbar>
      )}

      {/* Analytics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Total Suppliers</Typography>
              <Typography variant="h4">{analytics.totalSuppliers}</Typography>
              <LinearProgress variant="determinate" value={100} sx={{ mt: 1 }} />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Active Suppliers</Typography>
              <Typography variant="h4" color="success.main">{analytics.activeSuppliers}</Typography>
              <LinearProgress variant="determinate" value={80} sx={{ mt: 1 }} />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Total Orders</Typography>
              <Typography variant="h4">{analytics.totalOrders}</Typography>
              <LinearProgress variant="determinate" value={100} sx={{ mt: 1 }} />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Total Spend</Typography>
              <Typography variant="h4" color="info.main">${analytics.totalSpend}</Typography>
              <LinearProgress variant="determinate" value={60} sx={{ mt: 1 }} />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ width: '100%', mb: 2 }}>
        <Tabs
          value={tabIndex}
          onChange={(_, newValue) => setTabIndex(newValue)}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab label="Suppliers" />
          <Tab label="Purchase Orders" />
          <Tab label="Analytics" />
        </Tabs>
      </Paper>

      {tabIndex === 0 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Suppliers</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                placeholder="Search suppliers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filter.supplierStatus}
                  onChange={(e) => setFilter(prev => ({ ...prev, supplierStatus: e.target.value }))}
                  label="Status"
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleCreateSupplier}
                disabled={!hasPermission('supply:supplier:create')}
              >
                Add Supplier
              </Button>
            </Box>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Rating</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredSuppliers.map((supplier) => (
                  <TableRow key={supplier.id} hover>
                    <TableCell>{supplier.name}</TableCell>
                    <TableCell>{supplier.contact}</TableCell>
                    <TableCell>{supplier.email}</TableCell>
                    <TableCell>
                      <Chip label={supplier.status} color={supplier.status === 'active' ? 'success' : 'default'} size="small" />
                    </TableCell>
                    <TableCell>
                      <Rating value={supplier.rating} precision={0.1} readOnly size="small" />
                    </TableCell>
                    <TableCell>{new Date(supplier.createdAt).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => handleEditSupplier(supplier)} disabled={!hasPermission('supply:supplier:edit')}>
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => handleDeleteSupplier(supplier.id)} disabled={!hasPermission('supply:supplier:delete')}>
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {tabIndex === 1 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Purchase Orders</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                placeholder="Search orders..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filter.orderStatus}
                  onChange={(e) => setFilter(prev => ({ ...prev, orderStatus: e.target.value }))}
                  label="Status"
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="received">Received</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleCreateOrder}
                disabled={!hasPermission('supply:order:create')}
              >
                Add Order
              </Button>
            </Box>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Supplier</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Total</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Expected Delivery</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredOrders.map((order) => (
                  <TableRow key={order.id} hover>
                    <TableCell>{order.supplier.name}</TableCell>
                    <TableCell>
                      <Chip label={order.status} color={order.status === 'received' ? 'success' : order.status === 'pending' ? 'warning' : 'default'} size="small" />
                    </TableCell>
                    <TableCell>${order.total}</TableCell>
                    <TableCell>{new Date(order.createdAt).toLocaleDateString()}</TableCell>
                    <TableCell>{new Date(order.expectedDelivery).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => handleEditOrder(order)} disabled={!hasPermission('supply:order:edit')}>
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => handleDeleteOrder(order.id)} disabled={!hasPermission('supply:order:delete')}>
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {tabIndex === 2 && (
        <Box>
          <Typography variant="h6" gutterBottom>Supply Chain Analytics</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader title="Top Suppliers" />
                <CardContent>
                  <List>
                    {analytics.topSuppliers.map((supplier, index) => (
                      <ListItem key={index}>
                        <ListItemText
                          primary={supplier.name}
                          secondary={`Orders: ${supplier.totalOrders} | Spend: $${supplier.totalSpend}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader title="Order Trends" />
                <CardContent>
                  <List>
                    {analytics.orderTrends.map((trend, index) => (
                      <ListItem key={index}>
                        <ListItemText
                          primary={trend.date}
                          secondary={`Orders: ${trend.orders} | Spend: $${trend.spend}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Supplier/Order Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{dialogType === 'supplier' ? (selectedSupplier ? 'Edit Supplier' : 'Add Supplier') : (selectedOrder ? 'Edit Order' : 'Add Order')}</DialogTitle>
        <DialogContent>
          {dialogType === 'supplier' && (
            <Box>
              <TextField
                fullWidth
                label="Name"
                value={formSupplier.name || ''}
                onChange={e => setFormSupplier(prev => ({ ...prev, name: e.target.value }))}
                margin="normal"
              />
              <TextField
                fullWidth
                label="Contact"
                value={formSupplier.contact || ''}
                onChange={e => setFormSupplier(prev => ({ ...prev, contact: e.target.value }))}
                margin="normal"
              />
              <TextField
                fullWidth
                label="Email"
                value={formSupplier.email || ''}
                onChange={e => setFormSupplier(prev => ({ ...prev, email: e.target.value }))}
                margin="normal"
              />
              <TextField
                fullWidth
                label="Address"
                value={formSupplier.address || ''}
                onChange={e => setFormSupplier(prev => ({ ...prev, address: e.target.value }))}
                margin="normal"
              />
              <FormControl fullWidth margin="normal">
                <InputLabel>Status</InputLabel>
                <Select
                  value={formSupplier.status || 'active'}
                  onChange={e => setFormSupplier(prev => ({ ...prev, status: e.target.value as 'active' | 'inactive' }))}
                  label="Status"
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}
          {dialogType === 'order' && (
            <Box>
              <FormControl fullWidth margin="normal">
                <InputLabel>Supplier</InputLabel>
                <Select
                  value={formOrder.supplier?.id || ''}
                  onChange={e => {
                    const supplier = suppliers.find(s => s.id === e.target.value);
                    setFormOrder(prev => ({ ...prev, supplier }));
                  }}
                  label="Supplier"
                >
                  {suppliers.map(supplier => (
                    <MenuItem key={supplier.id} value={supplier.id}>{supplier.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label="Total"
                type="number"
                value={formOrder.total || ''}
                onChange={e => setFormOrder(prev => ({ ...prev, total: parseFloat(e.target.value) }))}
                margin="normal"
              />
              <FormControl fullWidth margin="normal">
                <InputLabel>Status</InputLabel>
                <Select
                  value={formOrder.status || 'pending'}
                  onChange={e => setFormOrder(prev => ({ ...prev, status: e.target.value as any }))}
                  label="Status"
                >
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="received">Received</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label="Expected Delivery"
                type="date"
                value={formOrder.expectedDelivery ? formOrder.expectedDelivery.substring(0, 10) : ''}
                onChange={e => setFormOrder(prev => ({ ...prev, expectedDelivery: e.target.value }))}
                margin="normal"
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            onClick={dialogType === 'supplier' ? handleSaveSupplier : handleSaveOrder}
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
